// SPDX-License-Identifier: AGPL-3.0-or-later

import {showChannelErrorModal} from '@app/features/channel/components/alerts/ChannelErrorModalUtils';
import styles from '@app/features/channel/components/ChannelNameField.module.css';
import type {FlatEmoji} from '@app/features/emoji/types/EmojiTypes';
import {ExpressionPickerSheet} from '@app/features/expressions/components/modals/ExpressionPickerSheet';
import {ExpressionPickerPopout} from '@app/features/expressions/components/popouts/ExpressionPickerPopout';
import {LexicalRichInput, type LexicalRichInputHandle} from '@app/features/lexical/composer/LexicalRichInput';
import {convertMarkdownToSegments} from '@app/features/messaging/utils/MarkdownToSegmentUtils';
import type {MentionSegment} from '@app/features/messaging/utils/TextareaSegmentManager';
import {shouldDisableAutofocusOnMobile} from '@app/features/platform/utils/AutofocusUtils';
import {remFromPx} from '@app/features/theme/layout/RemFromPx';
import formStyles from '@app/features/ui/components/form/FormInput.module.css';
import surfaceStyles from '@app/features/ui/components/form/FormSurface.module.css';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {Popout} from '@app/features/ui/popover/PopoverPopout';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import {CHANNEL_NAME_MAX_LENGTH} from '@fluxer/constants/src/LimitConstants';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {SmileyIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {forwardRef, type ReactNode, useCallback, useEffect, useId, useImperativeHandle, useRef, useState} from 'react';

const INSERT_EMOJI_DESCRIPTOR = msg({
	message: 'Insert emoji',
	comment: 'Accessible label for the emoji picker button on the channel name field.',
});
const CHANNEL_NAME_IS_TOO_LONG_DESCRIPTOR = msg({
	message: 'Channel name is too long',
	comment: 'Error modal title shown when inserting an emoji into a channel name would exceed the name limit.',
});
const SHORTEN_THE_CHANNEL_NAME_AND_TRY_AGAIN_DESCRIPTOR = msg({
	message: 'Shorten the channel name and try again.',
	comment: 'Body of the error modal shown when inserting an emoji into a channel name would exceed the name limit.',
});
const COMPOSER_SURFACE_INTERACTIVE_SELECTOR =
	'[data-channel-textarea], button, a, input, textarea, select, [role="button"]';

interface ChannelNameHydration {
	display: string;
	segments: Array<MentionSegment>;
	key: number;
}

function buildHydration(rawName: string, guildId: string, key: number): ChannelNameHydration {
	const converted = convertMarkdownToSegments(rawName, guildId);
	const segments = converted.segments.map((segment) => ({
		type: segment.type,
		id: segment.id,
		displayText: segment.displayText,
		actualText: segment.actualText,
		start: segment.start,
		end: segment.start + segment.displayText.length,
	}));
	return {display: converted.displayText, segments, key};
}

interface ChannelNameFieldProps {
	guildId: string;
	channelId?: string;
	initialName: string;
	label: string;
	placeholder?: string;
	error?: string;
	footer?: ReactNode;
	disabled?: boolean;
	autoFocus?: boolean;
	onNameChange: (name: string) => void;
}

export interface ChannelNameFieldHandle {
	syncFromMarkdown(markdown: string | null | undefined): void;
}

export const ChannelNameField = observer(
	forwardRef<ChannelNameFieldHandle, ChannelNameFieldProps>(
		(
			{
				guildId,
				channelId,
				initialName,
				label,
				placeholder = '',
				error,
				footer,
				disabled = false,
				autoFocus = false,
				onNameChange,
			},
			forwardedRef,
		) => {
			const {i18n} = useLingui();
			const editableId = useId();
			const labelId = `${editableId}-label`;
			const errorId = `${editableId}-error`;
			const composerRef = useRef<LexicalRichInputHandle | null>(null);
			const wrapperRef = useRef<HTMLDivElement | null>(null);
			const editableRef = useRef<HTMLElement | null>(null);
			const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
			const [hydration, setHydration] = useState(() => buildHydration(initialName, guildId, 0));
			const isMobile = MobileLayout.enabled;
			const hasError = error != null && error.length > 0;
			useImperativeHandle(
				forwardedRef,
				() => ({
					syncFromMarkdown(markdown) {
						const rawName = markdown ?? '';
						setHydration((previous) => buildHydration(rawName, guildId, previous.key + 1));
					},
				}),
				[guildId],
			);
			useEffect(() => {
				const wrapper = wrapperRef.current;
				if (wrapper == null) {
					editableRef.current = null;
					return;
				}
				const editable = wrapper.querySelector<HTMLElement>('[data-channel-textarea]');
				if (editable == null) {
					editableRef.current = null;
					return;
				}
				editableRef.current = editable;
				if (autoFocus && !disabled && !shouldDisableAutofocusOnMobile()) {
					const composer = composerRef.current;
					if (composer != null) {
						composer.focus();
					}
				}
			}, [autoFocus, disabled, hydration.key]);
			const handleChange = useCallback(
				(_display: string, _segments: Array<MentionSegment>, wire: string) => {
					onNameChange(wire);
				},
				[onNameChange],
			);
			const handleEmojiSelect = useCallback((emoji: FlatEmoji, shiftKey: boolean) => {
				const composer = composerRef.current;
				if (composer == null) {
					return false;
				}
				const didInsert = composer.insertEmoji(emoji);
				if (didInsert && shiftKey !== true) {
					setEmojiPickerOpen(false);
				}
				return didInsert;
			}, []);
			const handleExceedMaxLength = useCallback(() => {
				showChannelErrorModal({
					title: i18n._(CHANNEL_NAME_IS_TOO_LONG_DESCRIPTOR),
					message: i18n._(SHORTEN_THE_CHANNEL_NAME_AND_TRY_AGAIN_DESCRIPTOR),
					dataFlx: 'channel.channel-name-field.too-long-modal',
				});
			}, [i18n]);
			const emojiButton = isMobile ? (
				<FocusRing offset={-2} enabled={!disabled} data-flx="channel.channel-name-field.focus-ring">
					<button
						type="button"
						onClick={() => setEmojiPickerOpen(true)}
						className={clsx(styles.emojiButton, emojiPickerOpen && styles.emojiButtonActive)}
						disabled={disabled}
						aria-label={i18n._(INSERT_EMOJI_DESCRIPTOR)}
						aria-haspopup="dialog"
						aria-expanded={emojiPickerOpen}
						data-flx="channel.channel-name-field.emoji-button.open"
					>
						<SmileyIcon size={remFromPx(20)} weight="fill" data-flx="channel.channel-name-field.smiley-icon" />
					</button>
				</FocusRing>
			) : (
				<Popout
					position="bottom-end"
					animationType="none"
					offsetMainAxis={8}
					offsetCrossAxis={0}
					onOpen={() => setEmojiPickerOpen(true)}
					onClose={() => setEmojiPickerOpen(false)}
					returnFocusRef={editableRef}
					render={({onClose}) => (
						<ExpressionPickerPopout
							channelId={channelId}
							onEmojiSelect={(emoji, shiftKey) => {
								const didInsert = handleEmojiSelect(emoji, shiftKey === true);
								if (didInsert && shiftKey !== true) {
									onClose();
								}
							}}
							onClose={onClose}
							visibleTabs={['emojis']}
							data-flx="channel.channel-name-field.expression-picker-popout"
						/>
					)}
					data-flx="channel.channel-name-field.popout"
				>
					<FocusRing offset={-2} enabled={!disabled} data-flx="channel.channel-name-field.focus-ring--2">
						<button
							type="button"
							className={clsx(styles.emojiButton, emojiPickerOpen && styles.emojiButtonActive)}
							disabled={disabled}
							aria-label={i18n._(INSERT_EMOJI_DESCRIPTOR)}
							aria-haspopup="dialog"
							aria-expanded={emojiPickerOpen}
							data-flx="channel.channel-name-field.emoji-button"
						>
							<SmileyIcon size={remFromPx(20)} weight="fill" data-flx="channel.channel-name-field.smiley-icon--2" />
						</button>
					</FocusRing>
				</Popout>
			);
			return (
				<>
					<fieldset className={formStyles.fieldset} data-flx="channel.channel-name-field.fieldset">
						<div className={formStyles.labelContainer} data-flx="channel.channel-name-field.label-container">
							<label
								id={labelId}
								htmlFor={editableId}
								className={formStyles.label}
								onPointerDown={(event) => {
									if (disabled) {
										return;
									}
									event.preventDefault();
									const composer = composerRef.current;
									if (composer != null) {
										composer.focus();
									}
								}}
								data-flx="channel.channel-name-field.label"
							>
								{label}
							</label>
						</div>
						<div className={formStyles.inputGroup} data-flx="channel.channel-name-field.input-group">
							<FocusRing
								within={true}
								ringTarget={wrapperRef}
								focusTarget={wrapperRef}
								offset={-2}
								enabled={!disabled}
								data-flx="channel.channel-name-field.focus-ring--3"
							>
								<div
									ref={wrapperRef}
									className={clsx(styles.editorHost, surfaceStyles.surface, hasError && styles.editorHostError)}
									onPointerDown={(event) => {
										if (disabled) {
											return;
										}
										const target = event.target;
										if (target instanceof Element && target.closest(COMPOSER_SURFACE_INTERACTIVE_SELECTOR) != null) {
											return;
										}
										event.preventDefault();
										const composer = composerRef.current;
										if (composer != null) {
											composer.focus();
										}
									}}
									data-flx="channel.channel-name-field.editor-host"
								>
									<LexicalRichInput
										key={hydration.key}
										initialValue={hydration.display}
										initialSegments={hydration.segments}
										placeholder={placeholder}
										disabled={disabled}
										channel={null}
										allowedTriggers={['emoji']}
										allowSpecialMentions={false}
										markdown={false}
										singleLine={true}
										size="form"
										maxLength={CHANNEL_NAME_MAX_LENGTH}
										onExceedMaxLength={handleExceedMaxLength}
										className={styles.editor}
										autocompleteAnchor={wrapperRef.current}
										id={editableId}
										ariaLabelledBy={labelId}
										ariaInvalid={hasError}
										ariaErrorMessage={hasError ? errorId : undefined}
										richInputRef={composerRef}
										onChange={handleChange}
										onSubmit={() => undefined}
										i18n={i18n}
										data-flx="channel.channel-name-field.editor"
									/>
									{emojiButton}
								</div>
							</FocusRing>
							{hasError && (
								<span id={errorId} className={formStyles.errorText} data-flx="channel.channel-name-field.error-text">
									{error}
								</span>
							)}
						</div>
						{footer}
					</fieldset>
					{isMobile && (
						<ExpressionPickerSheet
							isOpen={emojiPickerOpen}
							onClose={() => setEmojiPickerOpen(false)}
							onEmojiSelect={(emoji, shiftKey) => {
								handleEmojiSelect(emoji, shiftKey === true);
							}}
							visibleTabs={['emojis']}
							channelId={channelId}
							data-flx="channel.channel-name-field.expression-picker-sheet"
						/>
					)}
				</>
			);
		},
	),
);
